import { checkIsInRichClient } from '@ds/swymkit/utils'

import { CatiaCollabEvent } from '~/entities/container/conversation/interfaces/events'
import { useMostRecentCollabActions } from '~/entities/container/conversation/stores/collab-events'
import { useConversationsStore } from '~/entities/container/conversation/stores/conversations'
import { useConversationsStoreV2 } from '~/entities/container/conversation/stores/v2-conversations'
import { useUsersStore } from '~/entities/user/user/stores/users'

import { useContentsStore } from '~/features-shared/content/stores/contents'
import { useContentsStoreV2 } from '~/features-shared/content/stores/v2-contents'
import { transientToITimeline } from '~/features-shared/rtconv/api/formatters'
import { RTInterfaceAPI } from '~/features-shared/rtconv/api/rtinterface-api'
import type { IRTTransientData, TxID } from '~/features-shared/rtconv/interfaces/rtinterface'
import type { ITimeline } from '~/features-shared/timeline/interfaces/timeline'

const inRichClient = checkIsInRichClient

/**
 * Returns the conversation object and it's id from the data
 *
 * @param data The data from the interface
 * @param handlerType name of the handler (for traces)
 * @returns The conversation object
 */
async function getConversationFromId(data: IRTTransientData, handlerType: string) {
  const { room_id } = data
  const convId = room_id.split('|')[0]
  const store = useConversationsStore()
  const conversation = await store.fetchConversation(convId)

  if (!conversation)
    throw new Error(`[${handlerType}] Conversation with id ${convId} not found`)

  return { conversation, convId }
}

/**
 * Util method to add message to timeline, update last message, and finish the transaction
 *
 * @param TxID The transaction id
 * @param convId The conversation id
 * @param timeline The timeline item
 */
async function addMessageToTimeline(TxID: string, convId: string, timeline: ITimeline) {
  if (
    !useConversationsStore()
      .isConversationFromV2(convId)
  ) {
    return addMessageToSwymTimeline(TxID, convId, timeline)
  }

  const store = useConversationsStoreV2()
  const storeMessages = useContentsStoreV2()
  storeMessages.addMessage(timeline, timeline.thread_id)

  store.updateConversationLastMessage(
    timeline.thread_id,
    timeline.id,
    timeline.created_at,
  )

  const storeUsers = useUsersStore()
  if (storeUsers.currentUser?.login === timeline.author.login)
    store.updateConversationLastMessageRead(convId, timeline.id)

  RTInterfaceAPI.triggerTxIDEvent({ TxID, data: timeline })
}

async function addMessageToSwymTimeline(TxID: string, convId: string, timeline: ITimeline) {
  const storeConversation = useConversationsStore()
  let conversation = storeConversation.getConversationById(timeline.community_subject_uri)

  if (!conversation)
    conversation = await storeConversation.fetchConversation(timeline.community_subject_uri)

  if (!conversation)
    throw new Error(`Conversation with id ${convId} not found`)

  const contentsStore = useContentsStore()
  contentsStore.addMessage(timeline)

  conversation.last_message = {
    message_id: timeline.id,
    // message_content: timeline.message_details.content,
    message_type: timeline.message_type,
    // model: timeline.message_details.model,
    // model_content: timeline.message_details.model_content,
    // message_title: timeline.message_details.title,
    // type: timeline.message_details.media_type,
    // preview_media_file_ext: timeline.message_details.preview_media_file_ext,
  }

  const isConversationUnread = storeConversation.isConversationUnread(conversation)
  if (conversation.is_hidden && isConversationUnread)
    conversation.is_hidden = false

  const storeUsers = useUsersStore()
  if (
    timeline.author.login === storeUsers.currentUser?.login // if connected user is the author
  )
    storeConversation.markAsRead(timeline.community_subject_uri)

  RTInterfaceAPI.triggerTxIDEvent({ TxID, data: timeline })
}

/**
 * Handles the "RTCMessengerCoReviewStop" event. It is sent when the collab event stops
 *
 * @param TxID Transaction ID
 * @param data {@link IRTTransientData Data} received from RTC server
 */
export async function RTCMessengerCoReviewStop(TxID: TxID, data?: IRTTransientData) {
  if (!data || !data.room_id || !inRichClient())
    return

  const { conversation, convId } = await getConversationFromId(data, 'RTCMessengerCoReviewStop')

  const timeline = await transientToITimeline({
    conversation,
    convId,
    date: data.date,
    messageId: data.message_id,
    serviceId: CatiaCollabEvent.CoReviewStop,
    TxID,
    userId: data.user_id,
  })

  if (!timeline)
    throw new Error(`[RTCMessengerCoReviewStop] Failed to format message`)

  const { updateRecentCollabAction } = useMostRecentCollabActions()
  updateRecentCollabAction({
    coReviewEvent: CatiaCollabEvent.CoReviewStop,
    conversationId: convId,
    messageId: '',
  })

  addMessageToTimeline(TxID, convId, timeline)
}

/**
 * Handles the "RTCMessengerCoReviewLeft" event. It is sent when a user leaves the collab session
 *
 * @param TxID Transaction ID
 * @param data {@link IRTTransientData Data} received from RTC server
 */
export async function RTCMessengerCoReviewLeft(TxID: TxID, data?: IRTTransientData) {
  if (!data || !data.room_id || !inRichClient())
    return

  const { conversation, convId } = await getConversationFromId(data, 'RTCMessengerCoReviewLeft')

  const timeline = await transientToITimeline({
    conversation,
    convId,
    date: data.date,
    messageId: data.message_id,
    serviceId: CatiaCollabEvent.CoReviewLeft,
    TxID,
    userId: data.user_id,
  })

  if (!timeline)
    throw new Error(`[RTCMessengerCoReviewLeft] Failed to format message`)

  const storeUsers = useUsersStore()
  if (storeUsers.currentUser?.login === timeline.author.login) {
    // Remove the banner
    const { updateRecentCollabAction } = useMostRecentCollabActions()
    updateRecentCollabAction({
      coReviewEvent: CatiaCollabEvent.CoReviewLeft,
      conversationId: timeline.thread_id,
      messageId: '',
    })
  }

  addMessageToTimeline(TxID, convId, timeline)
}

/**
 * Handles the "RTCMessengerReplyToCoreview" event. It is sent when a user joins a collab session
 *
 * @param TxID Transaction ID
 * @param data {@link IRTTransientData Data} received from RTC server
 */
export async function RTCMessengerReplyToCoreview(TxID: TxID, data?: IRTTransientData) {
  if (!data || !data.room_id || !inRichClient())
    return

  const { conversation, convId } = await getConversationFromId(data, 'RTCMessengerReplyToCoreview')

  const timeline = await transientToITimeline({
    conversation,
    convId,
    date: data.date,
    messageId: data.message_id,
    serviceId: CatiaCollabEvent.CoReviewAccept,
    TxID,
    userId: data.user_id,
  })

  if (!timeline)
    throw new Error(`[RTCMessengerReplyToCoreview] Failed to format message`)

  addMessageToTimeline(TxID, convId, timeline)
}

/**
 * Parser function for the lead change details
 *
 * @param data The string contents decoded
 * @returns The string to be stored or undefined if it wasn't accepted
 */
async function parseLeadChange(data: string): Promise<string | undefined> {
  // \x00\x00\x00}\x00\x1C\x00\b\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00\x00Yuxodtitp uxodtitp relinquished the Co-Review lead to Aniket AGRAWAL.\x00aal8@DEVOPSAAL87DQX2894637\x00leadChangeInfo,N,uxodtitp@DEVOPSAAL87DQX29934\x00

  const [_, parts] = data.split('leadChangeInfo,')

  const [accepted, userData] = parts.split(',')
  if (accepted !== 'Y' || !userData)
    return

  const [login] = userData.split('@')

  // This is the user's login
  return login
}

/**
 * Handles the "RTCMessengerAcceptLead" event. It is sent when a user accepts or rejects a lead change request in a collab session
 *
 * @param TxID Transaction ID
 * @param data {@link IRTTransientData Data} received from RTC server
 */
export async function RTCMessengerAcceptLead(TxID: TxID, data?: IRTTransientData) {
  if (!data || !data.room_id || !inRichClient())
    return

  const { conversation, convId } = await getConversationFromId(data, 'RTCMessengerAcceptLead')

  // "CATRTCTypedData:RTCQueueMessenger&AAAAfQAcAAgAAAAAAAAAAAAAAAAAAAAAAAAAAFl1eG9kdGl0cCB1eG9kdGl0cCByZWxpbnF1aXNoZWQgdGhlIENvLVJldmlldyBsZWFkIHRvIEFuaWtldCBBR1JBV0FMLgBhYWw4QERFVk9QU0FBTDg3RFFYMjg5NDYzNwA"
  const encodedData = data.message.split('application/octet-stream;CATRTCTypedData:RTCMessengerAcceptLead&')
    .pop()

  if (!encodedData)
    throw new Error(`[RTCMessengerAcceptLead] error in parsing message = ${data.message}`)

  const timeline = await transientToITimeline({
    conversation,
    convId,
    date: data.date,
    messageId: data.message_id,
    serviceId: CatiaCollabEvent.CoReviewLeadChange,
    TxID,
    userId: data.user_id,
    message: encodedData,
    parser: parseLeadChange,
    uri: false,
  })

  if (timeline)
    addMessageToTimeline(TxID, convId, timeline)
}

/**
 * Handles the "startCatiaCollab" event. It is sent when a collab activity is triggered in a swym conversation
 *
 * @param TxID Transaction ID
 * @param data data received from RTC server
 * @param data.convId The id of the swym conversation
 * @param data.msgId The id of the message
 * @param data.serviceId The service id corresponding to the event
 */
export async function startCatiaCollab(TxID: TxID, data?: {
  convId: string
  msgId: string
  serviceId: CatiaCollabEvent
}) {
  if (!data || !data.convId || !inRichClient())
    return

  const { useConversationsStoreV1 } = await import('~/entities/container/conversation/stores/v1-conversations')
  const store = useConversationsStoreV1()
  await store.logAVCallData(data)

  RTInterfaceAPI.triggerTxIDEvent({ TxID, data })
}
